import type {
  Address,
  AddressInput,
  Personnel,
  PersonnelInput,
} from "@/lib/brokerAccounts";
import type { AddressValue } from "@/lib/address";

// What an account PUT must carry back so a save does not destroy what it
// loaded (BRK-S223). PMS replaces each person wholesale and rewrites the
// legal/mailing Locations from the body, so anything a form loads but never
// shows has to go back out untouched. Shared by every surface that saves an
// account (AccountForm, the account record page).

/** A decimal string from the contract → the number the write side takes.
 *  Anything unparseable is treated as absent. */
export function parseDecimal(value: string | null): number | null {
  if (value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** The untouched fields of a loaded person, already in write shape. */
export type CarriedPerson = Omit<
  PersonnelInput,
  | "id"
  | "firstName"
  | "lastName"
  | "title"
  | "email"
  | "phones"
  | "roles"
  | "ownershipPercentage"
  | "spouse"
> & { spouseId?: string };

/** A null on the read is omitted (the write rejects null for most of these,
 *  and omitting it clears it anyway). SSN and driver's licence number are never
 *  read here: they are write-only, and omitting them keeps what PMS has. */
export function carriedFrom(p: Personnel): CarriedPerson {
  const remuneration = parseDecimal(p.annualRemuneration ?? null);
  const addresses = (p.addresses ?? []).map((a) => ({
    type: a.type,
    isPrimary: a.isPrimary,
    street: a.street,
    city: a.city,
    state: a.state,
    zip: a.zip,
    ...(a.addressLine2 ? { addressLine2: a.addressLine2 } : {}),
    ...(a.county ? { county: a.county } : {}),
  }));
  return {
    ...(p.duties ? { duties: p.duties } : {}),
    ...(p.birthDate ? { birthDate: p.birthDate } : {}),
    ...(p.wcInclusion ? { wcInclusion: p.wcInclusion } : {}),
    ...(remuneration != null ? { annualRemuneration: remuneration } : {}),
    ...(p.maritalStatus ? { maritalStatus: p.maritalStatus } : {}),
    ...(p.driversLicenseState
      ? { driversLicenseState: p.driversLicenseState }
      : {}),
    ...(addresses.length ? { addresses } : {}),
    ...(p.spouseId ? { spouseId: p.spouseId } : {}),
  };
}

/** A carried person back onto the wire. The spouse link goes out only when the
 *  spouse is still in this write: PMS 400s a link to anyone else, and a removed
 *  spouse means the broker ended the link. */
export function carriedInput(
  c: CarriedPerson | undefined,
  rosterIds: ReadonlySet<string>,
): Partial<PersonnelInput> {
  if (!c) return {};
  const { spouseId, ...rest } = c;
  return {
    ...rest,
    ...(spouseId && rosterIds.has(spouseId)
      ? { spouse: { personnelId: spouseId } }
      : {}),
  };
}

/** Line 2 and county for an address being sent, carried from the loaded
 *  address it still IS. Neither is editable in this portal, so they are kept
 *  only while street, city and state are unchanged; a different address must
 *  not inherit the old suite or county. */
export function addressExtras(
  sent: AddressValue,
  ...loaded: (Address | null | undefined)[]
): Pick<AddressInput, "addressLine2" | "county"> {
  const same = (a: string, b: string) =>
    a.trim().toLowerCase() === b.trim().toLowerCase();
  const match = loaded.find(
    (a) =>
      a &&
      same(a.street, sent.street) &&
      same(a.city, sent.city) &&
      same(a.state, sent.state),
  );
  if (!match) return {};
  return {
    ...(match.addressLine2 ? { addressLine2: match.addressLine2 } : {}),
    ...(match.county ? { county: match.county } : {}),
  };
}
